<script>
  import { Menu, MenuItem } from "@gl/design-system";
  import { navigate } from "../utils/index";
  import { navigate as navigateSvelte } from "svelte-routing";
  import { _, user } from "../stores/index";
  import { getContext } from "svelte";
  const { isAuthorized } = getContext("isAuthorized");
  import {
    faUser,
    faHatCowboy,
    faUsers,
    faTh,
    faHouse,
    faGrid,
    faTableRows,
    faKey,
  } from "@fortawesome/pro-regular-svg-icons";

  export let selected;

  /**
   * Make the content area a full-height flex column, capped to the viewport, with no
   * bottom padding. Set this on pages whose main content is a scrollable list/table with
   * its own `flex: 1; min-height: 0; overflow: auto` (see AssignmentGrid /
   * CustomTablePolicies): that element then fills the remaining space below the
   * breadcrumb/tabs and scrolls on its own instead of the whole page growing. Also needed
   * for pages ending in an <ActionFooter>: its `margin-top: auto` only pushes it to the
   * bottom inside a flex column, so without this it sits directly under short content
   * instead of at the bottom of the view. Dropping the bottom padding lets it sit flush.
   * @type {boolean}
   */
  export let fillHeight = false;
</script>

<div class="main">
  <div class="flex-menu theme-dark">
    <div class="menu">
      <Menu>
        {#if $isAuthorized("user_read")}
          <MenuItem
            selected={selected.startsWith("users")}
            on:click={() => navigate("/users")}
            on:keydown={(e) => e.key === "Enter" && navigate("/users")}
            icon={faUser}
          >
            <div class="label">
              {$_("common.users")}
            </div>
          </MenuItem>
        {/if}

        <div class="horizontal-line"></div>

        {#if $isAuthorized("role_read")}
          <MenuItem
            selected={selected.startsWith("roles")}
            on:click={() => navigate("/roles")}
            on:keydown={(e) => e.key === "Enter" && navigate("/roles")}
            icon={faHatCowboy}
          >
            <div class="label">
              {$_("common.roles")}
            </div>
          </MenuItem>
        {/if}

        {#if $isAuthorized("group_read")}
          <MenuItem
            selected={selected === "groups"}
            on:click={() => navigate("/groups")}
            on:keydown={(e) => e.key === "Enter" && navigate("/groups")}
            icon={faUsers}
          >
            <div class="label">
              {$_("common.groups")}
            </div>
          </MenuItem>
        {/if}

        {#if $isAuthorized("app_read")}
          <MenuItem
            selected={selected === "applications"}
            on:click={() => navigate("/applications")}
            on:keydown={(e) => e.key === "Enter" && navigate("/applications")}
            icon={faTh}
          >
            <div class="label">
              {$_("common.apps")}
            </div>
          </MenuItem>
        {/if}

        <div class="horizontal-line"></div>

        {#if $user.data.isMaster}
          <MenuItem
            selected={selected === "contracts"}
            on:click={() => navigateSvelte("/access-manager/admin/contracts")}
            on:keydown={(e) =>
              e.key === "Enter" &&
              navigateSvelte("/access-manager/admin/contracts")}
            icon={faHouse}
          >
            <div class="label">
              {$_("admin.contracts")}
            </div>
          </MenuItem>

          <MenuItem
            selected={selected === "authorizers"}
            on:click={() => navigateSvelte("/access-manager/admin/authorizers")}
            on:keydown={(e) =>
              e.key === "Enter" &&
              navigateSvelte("/access-manager/admin/authorizers")}
            icon={faKey}
          >
            <div class="label">
              {$_("admin.authorizers")}
            </div>
          </MenuItem>

          <MenuItem
            selected={selected === "globalApps"}
            on:click={() => navigateSvelte("/access-manager/admin/apps")}
            on:keydown={(e) =>
              e.key === "Enter" && navigateSvelte("/access-manager/admin/apps")}
            icon={faTableRows}
          >
            <div class="label">
              {$_("admin.apps")}
            </div>
          </MenuItem>
        {/if}
      </Menu>
    </div>
  </div>

  <div class="content" class:fill-height={fillHeight}>
    <slot />
  </div>
</div>

<style>
  .flex-menu {
    width: 262px;
    background-color: var(--gl-background);
  }

  .menu {
    position: fixed;
    height: 100%;
    padding: 14px;
    width: 262px;
    z-index: 10;
    background-color: var(--gl-background);
  }

  .content {
    margin: 0 auto;
    width: 75%;
    /* Same 48px the `p-6` utility used to apply, but set here so `fill-height` can drop
       one side (`p-6` is !important and would win). */
    padding: var(--gl-spacing-6);
  }

  /* See the `fillHeight` prop. A flex column is what lets an ActionFooter's
     `margin-top: auto` push it to the bottom, and what lets a `flex: 1; min-height: 0`
     list/table child fill the remaining space and scroll internally.

     `height: 100%` (not `min-height`) is load-bearing: it freezes the column at exactly
     .main's height so the flex:1 child has a fixed budget to fill and shrink into instead
     of the column growing with its content. No `overflow` is set here on purpose - a page
     without such a flex:1 child (e.g. the Global App > Policies tab) just overflows this
     box visibly and keeps scrolling via .content-container like before, unaffected. */
  .content.fill-height {
    display: flex;
    flex-direction: column;
    height: 100%;
    padding-bottom: 0;
  }

  .main {
    height: 100%;
    display: flex;
  }

  @media screen and (max-width: 1300px) {
    .menu,
    .flex-menu {
      width: 110px;
      padding: 0;
    }

    .menu :global(.gl-icon) {
      margin: 0;
    }
    .menu :global(.gl-menu-item) {
      flex-direction: column;
      text-align: center;
    }

    .label {
      font-size: 10px;
    }
  }

  .horizontal-line {
    border-top: 1px solid var(--gl-border);
    margin: 10px 0;
  }
</style>
